import { z } from 'zod';
import { checkInDateSchema } from '../check-ins/schema';
import { apiEnvelope } from '../envelope';
import { ianaTimezoneSchema } from '../timezone';

/**
 * The calendar frame the staff check-ins surface needs, on its OWN endpoint.
 *
 * This shape deliberately does NOT live on the member-detail read. ADR-135
 * Rule 4 makes a new endpoint the default for a change an old client should not
 * parse, and this is exactly that change: every field here is required, because
 * the only clients that can call this route are ones built with it. That is
 * what removes the whole class of mixed-version questions — a bundle predating
 * this route simply never asks, so there is no absent-versus-null to encode, no
 * default that has to be simultaneously tolerant and safe, and no installed
 * client that can be broken by adding a field later.
 *
 * It carries only what the check-ins surface reads, not a second copy of the
 * member projection: the member's current zone, and one row per enrolled
 * program with its member-local lifecycle days.
 */
export const memberCheckInCalendarProgramSchema = z.object({
  memberProgramId: z.string(),
  /** Programme key (`stimulants`, `alcohol`, …) — the surface's row label. */
  key: z.string(),
  /**
   * The member-local calendar day each lifecycle instant fell on, resolved
   * SERVER-side against the zone in effect AT that instant (ADR-095 Rule 10) —
   * not against the member's current zone, which would move a past day after
   * the member travels, and not client-side, where the zone timeline is not
   * visible at all.
   *
   * `null` means exactly one thing: there is no such instant. A null
   * `enrolledDay` is a programme that was never enrolled, and is therefore not
   * authorable; a null `endedDay` is a programme that has not ended, whose
   * window stays open above. The two are not symmetric and must not be
   * collapsed into one "missing bound" reading.
   */
  enrolledDay: checkInDateSchema.nullable(),
  endedDay: checkInDateSchema.nullable(),
});
export type MemberCheckInCalendarProgram = z.infer<typeof memberCheckInCalendarProgramSchema>;

export const memberCheckInCalendarDataSchema = z.object({
  /**
   * The member's CURRENT zone, resolved through the shared resolver (ADR-095
   * Rule 3), so it is never null and never an offset and no consumer re-decides
   * a fallback. It governs forward-looking windows only — today's marker, the
   * future gate, the month anchor, the week window and the fetch range. A past
   * day comes from the per-programme keys above, never from this (Rule 15).
   */
  timezone: ianaTimezoneSchema,
  programs: z.array(memberCheckInCalendarProgramSchema),
});
export type MemberCheckInCalendarData = z.infer<typeof memberCheckInCalendarDataSchema>;

export const memberCheckInCalendarResponseSchema = apiEnvelope(memberCheckInCalendarDataSchema);
export type MemberCheckInCalendarResponse = z.infer<typeof memberCheckInCalendarResponseSchema>;
